import { useState } from 'react';
import { useStore } from '../services/store';
import api, { extractErrorMessage } from '../services/api';
import { ArrowRight, ServerCog, ShieldCheck, Sparkles } from 'lucide-react';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useStore(s => s.setAuth);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username, password });
      setAuth(res.data.token, res.data.user);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studio-shell grid min-h-screen place-items-center px-4 py-8">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-lg border border-[var(--studio-line)] bg-white/45 shadow-[var(--studio-shadow)] backdrop-blur-xl lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative flex min-h-[520px] flex-col justify-between border-b border-[var(--studio-line)] p-6 sm:p-8 lg:border-b-0 lg:border-r">
          <div>
            <div className="flex items-center gap-3">
              <div className="studio-logo-wrap h-12 w-12">
                <img src="/assets/ddlab-logo.webp" alt="" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="text-2xl font-black leading-none text-[var(--studio-ink)]">DDStudio</h1>
                <p className="mt-1 text-xs font-black uppercase tracking-[0.16em] text-[var(--studio-blue)]">
                  studio.ddlab.cloud
                </p>
              </div>
            </div>
            <div className="factory-rail mt-5 h-1 w-56 rounded-full" />
          </div>

          <div className="grid gap-3">
            {[
              { icon: Sparkles, label: 'AI chat', detail: 'Claude and Gemini control' },
              { icon: ServerCog, label: 'Production panels', detail: 'Flowboard, Flowkit, Youwee, Codex' },
              { icon: ShieldCheck, label: 'Private console', detail: 'Authenticated DDlab workspace' },
            ].map(item => (
              <div key={item.label} className="studio-panel flex items-center gap-3 p-3">
                <span className="studio-section-icon h-9 w-9 text-[var(--studio-blue)]">
                  <item.icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-extrabold text-[var(--studio-ink)]">{item.label}</span>
                  <span className="block truncate text-xs font-semibold text-[var(--studio-muted)]">{item.detail}</span>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="flex items-center justify-center p-6 sm:p-8">
          <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[var(--studio-muted)]">Secure access</p>
              <h2 className="mt-2 text-2xl font-black text-[var(--studio-ink)]">Sign in</h2>
            </div>

            {error && (
              <div className="rounded border border-[#dc553c]/50 bg-[#dc553c]/10 px-3 py-2 text-sm font-semibold text-[#8c2d1f]">
                {error}
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-bold text-[var(--studio-ink-soft)]">Username</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="studio-input w-full rounded-lg px-3 py-2.5 focus:outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold text-[var(--studio-ink-soft)]">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="studio-input w-full rounded-lg px-3 py-2.5 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="studio-button-primary flex w-full items-center justify-center gap-2 rounded-lg py-2.5 font-extrabold transition-colors"
            >
              {loading ? 'Signing in...' : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
